import React from 'react';
import styled, { css } from 'styled-components';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { PX_TO_REM, SIZES } from '../../fundamentals/sizes/sizes';
import { DropdownItemContainer } from '../DropdownItem/DropdownItem';
import { StatusColour } from '../../fundamentals/colours/variants';
import { StatusIcon } from '../../components/StatusIcon/StatusIcon';
import { Heading6 } from '../../components/Headings/Headings';
import { CloseButton, ICloseButtonProps } from '../../components/CloseButton/CloseButton';
import { IGapSizeProps, IGutterProps, useGutterCss } from '../../fundamentals/spaces/space';
import { CtaButtonBar, ICtaButtonBarProps } from '../../components/CtaButtonBar/CtaButtonBar';
import { DropdownMenu } from '../Dropdown/DropdownMenu';
import { useDropdown } from '../Dropdown/useDropdown';
import { Badge, IBadgeProps } from '../../components/Badge/Badge';
import { useDropdownMenu } from 'react-overlays/DropdownMenu';
import { GapContainer } from '../../fundamentals/spaces/SpaceContainer';

export type MiniModalSize = typeof SIZES.X_SMALL | typeof SIZES.SMALL | typeof SIZES.MEDIUM | typeof SIZES.LARGE;
const MiniModalWidths: Record<MiniModalSize, string> = {
  [SIZES.X_SMALL]: PX_TO_REM[200], // NOTE: Figma says 416px, but this size is not available
  [SIZES.SMALL]: PX_TO_REM[400], // NOTE: Figma says 416px, but this size is not available
  [SIZES.MEDIUM]: PX_TO_REM[608],
  [SIZES.LARGE]: PX_TO_REM[800]
};

export interface IMiniModalProps extends React.PropsWithChildren<Pick<ICtaButtonBarProps, 'ctaButtons'>> {
  size: MiniModalSize;
  headerLabel?: string;
  headerStatus?: StatusColour;
  headerBadge?: Omit<IBadgeProps, 'size' | 'light'>;
  bodyGap?: IGapSizeProps['gap'];
  hideClose?: boolean;
  noHeader?: boolean;
}

export const MiniModalDropdownMenu = styled(DropdownMenu)<{ size: MiniModalSize; isMobile?: boolean }>`
  ${({ size, isMobile }) =>
    !isMobile &&
    css`
      min-width: ${MiniModalWidths[size]};
      max-width: ${MiniModalWidths[size]};
    `}
`;

const MiniModalDropdownItemContainer = styled(DropdownItemContainer)<IGutterProps & IGapSizeProps>`
  flex-direction: column;
  ${useGutterCss()};
`;

export const MiniModalHeaderContainer = styled(GapContainer)`
  width: 100%;
  ${CloseButton} {
    margin-left: auto;
  }
`;

export const MiniModalBodyContainer = styled(GapContainer)`
  width: 100%;
`;

const MiniModalCtaButtonBar = styled(CtaButtonBar)`
  width: 100%;
`;

const MiniModalCloseButton: React.FC<ICloseButtonProps> = ({ ...rest }) => {
  const { isMobile, setHovered, setPinned } = useDropdown({});
  const [, { show, toggle }] = useDropdownMenu({});

  const handleClick = () => {
    if (isMobile) toggle?.(!show);
    else {
      setHovered?.(false);
      setPinned?.(false);
    }
  };

  return <CloseButton onClick={handleClick} {...rest} />;
};

export const MiniModal: React.FC<IMiniModalProps> = ({
  headerStatus,
  headerLabel,
  headerBadge,
  ctaButtons,
  hideClose,
  size,
  bodyGap = false,
  children,
  noHeader = false,
  ...rest
}) => {
  const { generateTestId } = useTestId(rest, 'MiniModal');
  const { isMobile } = useDropdown(rest);

  return (
    <MiniModalDropdownMenu size={size} isMobile={isMobile} {...rest} {...generateTestId()}>
      <MiniModalDropdownItemContainer gutter={16} gap={16} {...generateTestId('Container')}>
        {!noHeader && (
          <MiniModalHeaderContainer gap={16} {...generateTestId('Header.Header')}>
            {headerStatus && <StatusIcon variant={headerStatus} size={SIZES.X_SMALL} {...generateTestId('Header.Status')} />}
            {headerBadge && <Badge size={SIZES.X_SMALL} light {...headerBadge} {...generateTestId('Header.Badge')} />}

            <Heading6 gutter={false} {...generateTestId('Header.Label')}>
              {headerLabel}
            </Heading6>

            {!hideClose && <MiniModalCloseButton size={SIZES.X_SMALL} {...generateTestId('Header.Close')} />}
          </MiniModalHeaderContainer>
        )}
        <MiniModalBodyContainer column gap={bodyGap} {...generateTestId('Body')}>
          {children}
        </MiniModalBodyContainer>
        <MiniModalCtaButtonBar ctaButtons={ctaButtons} {...generateTestId('CtaButtonBar')} />
      </MiniModalDropdownItemContainer>
    </MiniModalDropdownMenu>
  );
};
